import { z } from 'zod';
import { directoryProviderSchema } from './directory-provider.js';
import { employmentStatusSchema } from './person.js';
import { SCIM_TOKEN_PREFIX } from './scim.js';
// ---- Providers -------------------------------------------------------------------------
export { DIRECTORY_PROVIDERS, directoryProviderSchema } from './directory-provider.js';
export const DIRECTORY_PROVIDER_SLUGS = ['google', 'entra', 'scim'];
export const directoryProviderSlugSchema = z.enum(DIRECTORY_PROVIDER_SLUGS);
const SLUG_TO_PROVIDER = {
    google: 'GOOGLE',
    entra: 'ENTRA',
    scim: 'SCIM',
};
const PROVIDER_TO_SLUG = {
    GOOGLE: 'google',
    ENTRA: 'entra',
    SCIM: 'scim',
};
export function directoryProviderFromSlug(slug) {
    return SLUG_TO_PROVIDER[slug];
}
export function directoryProviderSlug(provider) {
    return PROVIDER_TO_SLUG[provider];
}
// ---- Lifecycle enums (mirrored by Prisma enums) ------------------------------------------
export const DIRECTORY_CONNECTION_STATUSES = [
    'ACTIVE',
    'NEEDS_RECONNECT',
    'PAUSED',
    'DISCONNECTED',
];
export const directoryConnectionStatusSchema = z.enum(DIRECTORY_CONNECTION_STATUSES);
export const DIRECTORY_SYNC_TRIGGERS = ['SCHEDULED', 'MANUAL', 'PREVIEW', 'SCIM'];
export const directorySyncTriggerSchema = z.enum(DIRECTORY_SYNC_TRIGGERS);
export const DIRECTORY_SYNC_RUN_STATUSES = [
    'RUNNING',
    'PREVIEWED',
    'APPLIED',
    'HELD_FOR_APPROVAL',
    'DISCARDED',
    'FAILED',
];
export const directorySyncRunStatusSchema = z.enum(DIRECTORY_SYNC_RUN_STATUSES);
export const DIRECTORY_SCOPE_KINDS = ['ORG_UNIT', 'GROUP'];
export const directoryScopeKindSchema = z.enum(DIRECTORY_SCOPE_KINDS);
export const DIRECTORY_SYNC_WARNING_CODES = [
    'MISSING_EMAIL',
    'INVALID_EMAIL',
    'DUPLICATE_EMAIL',
    'EMAIL_TAKEN',
    'INVALID_NAME',
    'TITLE_INVALID',
    'DEPARTMENT_INVALID',
    'DEPARTMENT_AMBIGUOUS',
    'MANAGER_UNRESOLVED',
    'MANAGER_CYCLE',
    'UNMAPPED_LOCATION',
    'OUT_OF_SCOPE',
    'SCOPE_ITEM_MISSING',
    'OPTED_OUT',
    'PHONE_INVALID',
    /** `platform.max_people`: the plan's people cap is full, so this create/reactivation was skipped (never a refusal). */
    'PEOPLE_LIMIT_REACHED',
];
export const directorySyncWarningCodeSchema = z.enum(DIRECTORY_SYNC_WARNING_CODES);
export const DIRECTORY_SYNC_UNMATCHED_DECISIONS = ['KEEP_MANUAL', 'DEACTIVATE'];
export const directorySyncUnmatchedDecisionSchema = z.enum(DIRECTORY_SYNC_UNMATCHED_DECISIONS);
// ---- Constants ---------------------------------------------------------------------------
export const DIRECTORY_SYNC_INTERVAL_HOURS_MIN = 1;
export const DIRECTORY_SYNC_INTERVAL_HOURS_MAX = 168;
export const DIRECTORY_SYNC_INTERVAL_HOURS_DEFAULT = 24;
export const directorySyncIntervalHoursSchema = z
    .number()
    .int()
    .min(DIRECTORY_SYNC_INTERVAL_HOURS_MIN)
    .max(DIRECTORY_SYNC_INTERVAL_HOURS_MAX);
/** PATCH …/directory-sync/schedule. At least one key; `paused` is the ADMIN's switch (Technical Notes (d)). */
export const updateDirectorySyncScheduleSchema = z
    .object({
    intervalHours: directorySyncIntervalHoursSchema.optional(),
    paused: z.boolean().optional(),
})
    .strict()
    .refine((v) => v.intervalHours !== undefined || v.paused !== undefined, {
    message: 'EMPTY_UPDATE',
});
/** The settings page's interval form (zodResolver). */
export const directorySyncIntervalFormSchema = z
    .object({ intervalHours: directorySyncIntervalHoursSchema })
    .strict();
// Field ownership (Technical Notes (h); phone since Phase 2a); the PATCH /people/:id keys a sync owns.
export const DIRECTORY_SYNCED_PERSON_FIELDS = [
    'firstName',
    'lastName',
    'email',
    'title',
    'departmentId',
    'managerId',
    'primaryLocationId',
    'employmentStatus',
    'phone',
];
export const DIRECTORY_SYNC_MAX_SCOPE_ITEMS = 50;
export const DIRECTORY_SYNC_MAX_STORED_WARNINGS = 500;
export const DIRECTORY_SYNC_PREVIEW_TTL_MINUTES = 30;
export const DIRECTORY_SYNC_WARNING_DETAIL_MAX = 500;
/** The safety brake (owner decision 2026-10-20, Technical Notes (a)). A run that would deactivate MORE THAN
 *  either limit is held for approval instead of applied. Owner-fixed numbers: changing them is a code change. */
export const DIRECTORY_SYNC_BRAKE_MAX_PERCENT = 10;
export const DIRECTORY_SYNC_BRAKE_MAX_COUNT = 25;
/** The SCIM safety brake's rolling window (S49-3u50 Phase 4b). Owner-fixed like the limits it applies. */
export const DIRECTORY_SYNC_SCIM_BRAKE_WINDOW_MINUTES = 60;
// ---- The port's normalized output (an adapter safeParses vendor JSON INTO these) ---------
export const sourcePersonSchema = z
    .object({
    externalId: z.string().min(1).max(255), // provider's immutable id
    email: z.string().max(320).nullable(), // raw; the planner validates it
    firstName: z.string().max(200), // raw; may be '' - the planner validates it
    lastName: z.string().max(200),
    title: z.string().max(500).nullable(),
    department: z.string().max(500).nullable(),
    managerEmail: z.string().max(320).nullable(),
    // raw, as the provider stores it; the planner normalises it to E.164 or warns `PHONE_INVALID`.
    // Absent or null means the provider has no mobile number.
    phone: z.string().max(64).nullable().optional(),
    suspended: z.boolean(),
    orgUnitPath: z.string().max(2048).nullable(), // null = provider has no OUs
    groupIds: z.array(z.string().min(1).max(255)).max(1000), // only ids from the groups asked about
})
    .strict();
export const sourceOrgUnitSchema = z
    .object({
    id: z.string().min(1).max(255),
    path: z.string().min(1).max(2048),
    name: z.string().min(1).max(255),
})
    .strict();
export const sourceGroupSchema = z
    .object({
    id: z.string().min(1).max(255),
    name: z.string().min(1).max(255),
    email: z.string().max(320).nullable(),
})
    .strict();
// ---- Connection ----------------------------------------------------------------------------
export const directoryScopeItemDTOSchema = z
    .object({
    kind: directoryScopeKindSchema,
    sourceId: z.string().min(1).max(255),
    displayName: z.string().min(1).max(2048), // OU path or group name at selection time
    locationId: z.string().min(1).nullable(), // null = this item maps to no location
})
    .strict();
export const directoryScimTokenSummarySchema = z
    .object({
    tokenHint: z.string().length(4),
    tokenIssuedAt: z.string().datetime(),
    heldRunId: z.string().min(1).optional(), // present iff a SCIM HELD_FOR_APPROVAL run is open
})
    .strict();
export const directoryConnectionDTOSchema = z
    .object({
    id: z.string().min(1),
    provider: directoryProviderSchema,
    status: directoryConnectionStatusSchema,
    externalTenantName: z.string(), // the customer's domain, not a person
    scope: z.array(directoryScopeItemDTOSchema),
    intervalHours: z.number().int(),
    paused: z.boolean(),
    connectedAt: z.string().datetime(),
    firstAppliedAt: z.string().datetime().nullable(),
    lastRunAt: z.string().datetime().nullable(),
    lastSuccessAt: z.string().datetime().nullable(),
    nextRunAt: z.string().datetime().nullable(),
    scim: directoryScimTokenSummarySchema.optional(), // present iff provider === 'SCIM'; never the token
})
    .strict();
/** POST …/directory-sync/scim/token (201). The ONLY response that ever carries the plaintext token. */
export const directorySyncScimTokenDTOSchema = z
    .object({
    token: z.string().regex(new RegExp(`^${SCIM_TOKEN_PREFIX}[A-Za-z0-9_-]{43}$`)),
    tokenHint: z.string().length(4),
    tokenIssuedAt: z.string().datetime(),
})
    .strict();
export const directorySyncStatusDTOSchema = z
    .object({
    providers: z.array(z.object({ provider: directoryProviderSchema, available: z.boolean() }).strict()),
    connection: directoryConnectionDTOSchema.nullable(), // null = never connected, or DISCONNECTED
})
    .strict();
export const directorySyncConnectStartDTOSchema = z
    .object({ authorizeUrl: z.string().url() })
    .strict();
export const completeDirectorySyncConnectSchema = z
    .object({
    code: z.string().min(1).max(4096),
    state: z.string().min(1).max(4096),
})
    .strict();
export const directorySourceCatalogDTOSchema = z
    .object({
    orgUnits: z.array(sourceOrgUnitSchema),
    groups: z.array(sourceGroupSchema),
})
    .strict();
export const updateDirectorySyncScopeSchema = z
    .object({
    items: z.array(directoryScopeItemDTOSchema).max(DIRECTORY_SYNC_MAX_SCOPE_ITEMS),
})
    .strict()
    .superRefine((value, ctx) => {
    const seen = new Set();
    value.items.forEach((item, i) => {
        const key = `${item.kind}:${item.sourceId}`;
        if (seen.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'DUPLICATE_SCOPE_ITEM',
                path: ['items', i],
            });
        }
        seen.add(key);
    });
});
// ---- Runs ----------------------------------------------------------------------------------
export const directorySyncCountsSchema = z
    .object({
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    linked: z.number().int().nonnegative(),
    reactivated: z.number().int().nonnegative(),
    deactivated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    absent: z.number().int().nonnegative(), // synced people the source no longer has in scope
})
    .strict();
export const EMPTY_DIRECTORY_SYNC_COUNTS = Object.freeze({
    created: 0,
    updated: 0,
    linked: 0,
    reactivated: 0,
    deactivated: 0,
    skipped: 0,
    unchanged: 0,
    absent: 0,
});
export const directorySyncWarningSchema = z
    .object({
    code: directorySyncWarningCodeSchema,
    externalId: z.string().min(1).max(255).nullable(),
    personId: z.string().min(1).nullable(),
    name: z.string().max(401).nullable(), // the source person's display name; PII - never logged
    detail: z.string().max(DIRECTORY_SYNC_WARNING_DETAIL_MAX).nullable(), // never a credential
})
    .strict();
export const directorySyncRunSummaryDTOSchema = z
    .object({
    id: z.string().min(1),
    trigger: directorySyncTriggerSchema,
    status: directorySyncRunStatusSchema,
    counts: directorySyncCountsSchema,
    warningCount: z.number().int().nonnegative(), // total, including any not stored
    failureCode: z.string().nullable(), // an ERROR_CODES member when FAILED
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime().nullable(),
})
    .strict();
export const directorySyncUnmatchedPersonDTOSchema = z
    .object({
    personId: z.string().min(1),
    name: z.string().min(1),
    email: z.string().nullable(),
    title: z.string().nullable(),
    employmentStatus: employmentStatusSchema,
})
    .strict();
/** Present on a HELD_FOR_APPROVAL run only: who it would make inactive, out of how many synced ACTIVE people. */
export const directorySyncHeldDTOSchema = z
    .object({
    syncedActiveCount: z.number().int().nonnegative(),
    deactivations: z.array(directorySyncUnmatchedPersonDTOSchema),
})
    .strict();
export const directorySyncRunDetailDTOSchema = directorySyncRunSummaryDTOSchema
    .extend({
    warnings: z.array(directorySyncWarningSchema),
    warningsTruncated: z.number().int().nonnegative(),
    unmatchedLocal: z.array(directorySyncUnmatchedPersonDTOSchema), // only non-empty on a PREVIEWED run
    previewExpiresAt: z.string().datetime().nullable(), // startedAt + TTL for PREVIEWED, else null
    held: directorySyncHeldDTOSchema.optional(),
})
    .strict();
export const DIRECTORY_SYNC_NOW_OUTCOMES = ['APPLIED', 'QUEUED', 'HELD'];
export const directorySyncNowOutcomeSchema = z.enum(DIRECTORY_SYNC_NOW_OUTCOMES);
/** POST …/sync-now. APPLIED and HELD carry the run; QUEUED (the run outgrew the request's budget, Technical Notes (f)) carries null. */
export const directorySyncNowResultDTOSchema = z
    .object({
    outcome: directorySyncNowOutcomeSchema,
    run: directorySyncRunDetailDTOSchema.nullable(),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.outcome !== 'QUEUED') !== (v.run !== null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'RUN_MATCHES_OUTCOME', path: ['run'] });
    }
});
export const applyDirectorySyncSchema = z
    .object({
    decisions: z
        .array(z
        .object({
        personId: z.string().min(1).max(64),
        decision: directorySyncUnmatchedDecisionSchema,
    })
        .strict())
        .max(5000),
})
    .strict()
    .superRefine((value, ctx) => {
    const seen = new Set();
    value.decisions.forEach((d, i) => {
        if (seen.has(d.personId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'DUPLICATE_DECISION',
                path: ['decisions', i],
            });
        }
        seen.add(d.personId);
    });
});
/** PUT …/directory-sync/people/:personId/opt-out. */
export const setDirectorySyncOptOutSchema = z.object({ optedOut: z.boolean() }).strict();
//# sourceMappingURL=directory-sync.js.map